// frontend/src/operator/OperatorDataFormats.js
// 新規施設導入時に準備が必要なデータのフォーマット一覧 + テンプレ CSV ダウンロード
// - 各テンプレは runtime で組立ててダウンロード (追加ファイル不要)

import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import JSZip from 'jszip';

const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';

// =============================================================================
// テンプレ定義
// =============================================================================
const TEMPLATES = [
  {
    id: 'staff',
    title: 'スタッフ',
    description: '所属する看護師 / 医師 / クラーク等の名簿。 employee_id は組織内で一意。',
    filename: 'staff_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'employee_id', required: true,  desc: '社員番号 (組織内で一意)。 例: 2100212010' },
      { name: 'last_name',   required: true,  desc: '姓' },
      { name: 'first_name',  required: true,  desc: '名' },
      { name: 'email',       required: true,  desc: 'メールアドレス (ログイン用)' },
      { name: 'job_type',    required: true,  desc: '職種名 (別途 職種マスタと一致)' },
      { name: 'year',        required: false, desc: '入職年 (YYYY)。 例: 2024' },
      { name: 'role',        required: false, desc: 'admin / staff。 未指定は staff' },
      { name: 'day_shift_only',      required: false, desc: 'true/false。 日勤のみのスタッフか' },
      { name: 'day_shift_only_mode', required: false, desc: 'all / weekdays。 day_shift_only=true 時のみ' },
    ],
    samples: [
      { employee_id: '2024001', last_name: '田中', first_name: '太郎', email: 'tanaka@example.com', job_type: '看護師', year: 2024, role: 'staff', day_shift_only: 'false', day_shift_only_mode: '' },
      { employee_id: '2020015', last_name: '山田', first_name: '花子', email: 'yamada@example.com', job_type: '看護師', year: 2020, role: 'admin', day_shift_only: 'false', day_shift_only_mode: '' },
      { employee_id: '2018005', last_name: '佐藤', first_name: '一郎', email: 'sato@example.com',  job_type: '看護師', year: 2018, role: 'staff', day_shift_only: 'true',  day_shift_only_mode: 'weekdays' },
    ],
  },
  {
    id: 'job_types',
    title: '職種マスタ',
    description: '看護師 / 看護助手 / クラーク等の職種一覧。 スタッフ登録時の job_type と一致させる。',
    filename: 'job_types_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'name',          required: true,  desc: '職種名。 例: 看護師' },
      { name: 'display_order', required: false, desc: '並び順 (整数)。 例: 1' },
    ],
    samples: [
      { name: '看護師', display_order: 1 },
      { name: '看護助手', display_order: 2 },
      { name: 'クラーク', display_order: 3 },
    ],
  },
  {
    id: 'departments',
    title: '診療科',
    description: '手術対象の診療科一覧。 術式に紐付ける。',
    filename: 'departments_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'name',          required: true,  desc: '診療科名。 例: 心臓血管外科' },
      { name: 'display_order', required: false, desc: '並び順' },
    ],
    samples: [
      { name: '心臓血管外科', display_order: 1 },
      { name: '消化器外科',   display_order: 2 },
      { name: '整形外科',     display_order: 3 },
    ],
  },
  {
    id: 'procedures',
    title: '術式マスタ',
    description: '手術申込で使う術式一覧。 診療科に紐付け。',
    filename: 'procedures_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'name',            required: true,  desc: '術式名' },
      { name: 'department',      required: true,  desc: '診療科名 (departments と一致)' },
      { name: 'level',           required: false, desc: 'レベル名 (★1 / 初級 等。 procedure_levels 設定後)' },
      { name: 'required_roles',  required: false, desc: '必要役割 JSON。 例: [{"type":"scrub","label":"器械"}]' },
    ],
    samples: [
      { name: '虫垂切除術',        department: '消化器外科', level: '★1', required_roles: '[{"type":"scrub"},{"type":"circulating"}]' },
      { name: '胃癌手術',          department: '消化器外科', level: '★2', required_roles: '[{"type":"scrub"},{"type":"circulating"}]' },
      { name: '冠動脈バイパス術',  department: '心臓血管外科', level: '★3', required_roles: '[{"type":"scrub"},{"type":"circulating"}]' },
    ],
  },
  {
    id: 'shift_patterns',
    title: '勤務パターン',
    description: '日勤 / 夜勤 / 明け 等の勤務種別マスタ。 短縮名 (displayMark) は月表シフト表で使う。',
    filename: 'shift_patterns_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'name',              required: true,  desc: 'パターン名。 例: 日勤 (看護師用)' },
      { name: 'display_mark',      required: true,  desc: 'シフト表の表示記号。 例: ◯N' },
      { name: 'start_time',        required: true,  desc: 'HH:MM' },
      { name: 'end_time',          required: true,  desc: 'HH:MM' },
      { name: 'target_job_types',  required: false, desc: '対象職種 (カンマ区切り)。 例: 看護師' },
      { name: 'flexibility_type',  required: false, desc: 'fixed / flexible。 未指定は flexible' },
      { name: 'required_count',    required: false, desc: '必要人数 (曜日 JSON)。 例: {"月":47,"火":47,...}' },
      { name: 'post_shift_ids',    required: false, desc: '後続シフト (別 pattern の name をカンマ区切り)' },
    ],
    samples: [
      { name: '日勤(看護師用)', display_mark: '◯N', start_time: '08:30', end_time: '17:00', target_job_types: '看護師', flexibility_type: 'fixed', required_count: '{"月":47,"火":47,"水":47,"木":47,"金":47,"土":10,"日":8}', post_shift_ids: '' },
      { name: '夜勤(看護師用)', display_mark: '◯Y', start_time: '16:30', end_time: '09:00', target_job_types: '看護師', flexibility_type: 'fixed', required_count: '{"月":5,"火":5,"水":5,"木":5,"金":5,"土":5,"日":5}', post_shift_ids: '明け(看護師用)' },
      { name: '明け(看護師用)', display_mark: '◯A', start_time: '00:00', end_time: '09:00', target_job_types: '看護師', flexibility_type: 'flexible', required_count: '', post_shift_ids: '' },
    ],
  },
  {
    id: 'skill_groups',
    title: 'スキルグループ',
    description: '「日勤可」「夜勤可」「オンコール可」など任意グループ。 メンバー割り当てはインポート後 UI で。',
    filename: 'skill_groups_template.csv',
    priority: '推奨',
    priorityColor: '#f59e0b',
    columns: [
      { name: 'name',        required: true,  desc: 'グループ名' },
      { name: 'description', required: false, desc: '説明' },
    ],
    samples: [
      { name: '日勤専任',       description: '日勤のみに配置される' },
      { name: '夜勤可',         description: '夜勤配置可能' },
      { name: '2026年度1年目',   description: '新人 (必要人数カウント対象外)' },
    ],
  },
  {
    id: 'holidays',
    title: '祝日',
    description: '国民の祝日 + 組織独自の休業日。 内閣府公表 API から自動取得も可 (別途)。',
    filename: 'holidays_template.csv',
    priority: '推奨',
    priorityColor: '#f59e0b',
    columns: [
      { name: 'date',       required: true,  desc: 'YYYY-MM-DD' },
      { name: 'name',       required: false, desc: '祝日名' },
      { name: 'is_public',  required: false, desc: 'true/false。 公的祝日か組織独自か' },
    ],
    samples: [
      { date: '2026-01-01', name: '元日', is_public: 'true' },
      { date: '2026-01-12', name: '成人の日', is_public: 'true' },
    ],
  },
  {
    id: 'holiday_policy',
    title: '休日ポリシー',
    description: '月の公休上限 / 下限 / 連勤上限。 組織で 1 レコード。',
    filename: 'holiday_policy_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'monthly_min',              required: true,  desc: '月の公休下限 (整数)。 例: 8' },
      { name: 'monthly_max',              required: true,  desc: '月の公休上限。 例: 11' },
      { name: 'annual_target',            required: false, desc: '年間休日目標。 例: 120' },
      { name: 'max_consecutive_work_days', required: true, desc: '連勤上限。 例: 5.5' },
      { name: 'weekly_baseline',          required: false, desc: '週基準日数' },
      { name: 'initial_password_prefix',  required: false, desc: '初期パスワード接頭辞。 例: hospital' },
    ],
    samples: [
      { monthly_min: 8, monthly_max: 11, annual_target: 120, max_consecutive_work_days: 5.5, weekly_baseline: 5, initial_password_prefix: 'hospital' },
    ],
  },
  {
    id: 'rooms',
    title: '手術室',
    description: '手術室の識別記号一覧。 手術申込 CSV の room 列と完全一致で紐付け。',
    filename: 'rooms_template.csv',
    priority: '必須',
    priorityColor: '#dc2626',
    columns: [
      { name: 'id',   required: true, desc: '部屋の識別記号。 例: OR1 / OR2' },
      { name: 'name', required: false, desc: '表示名 (未指定なら id を表示)' },
    ],
    samples: [
      { id: 'OR1', name: '第1手術室' },
      { id: 'OR2', name: '第2手術室' },
      { id: 'OR3', name: '第3手術室' },
    ],
  },
];

// =============================================================================
// CSV 生成
// =============================================================================
const escapeCsv = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

// テンプレの中身 (BOM 付き UTF-8 CSV 文字列) を生成
const buildCsvContent = (template) => {
  const headers = template.columns.map((c) => c.name);
  const commentLines = [
    `# ScrubEdge 導入テンプレート: ${template.title}`,
    `# 生成: ${dayjs().format('YYYY-MM-DD HH:mm')}`,
    `# 必須列: ${template.columns.filter((c) => c.required).map((c) => c.name).join(', ') || 'なし'}`,
    `# コメント行 (# 始まり) は取込時にスキップされます`,
    ``,
  ];
  const rows = template.samples.map((row) =>
    headers.map((h) => escapeCsv(row[h] ?? '')).join(',')
  );
  return commentLines.join('\n') + '\n' + headers.join(',') + '\n' + rows.join('\n') + '\n';
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadCsv = (template) => {
  const bom = '﻿';
  const blob = new Blob([bom + buildCsvContent(template)], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, template.filename);
};

// 全テンプレを 1 つの ZIP にまとめる (README.txt 付き)
const downloadAllAsZip = async () => {
  const zip = new JSZip();
  const bom = '﻿';
  // 各 CSV を追加
  TEMPLATES.forEach((t) => {
    zip.file(t.filename, bom + buildCsvContent(t));
  });
  // README.txt を追加
  const readme = [
    'ScrubEdge 新規施設導入 データテンプレート一覧',
    `生成: ${dayjs().format('YYYY-MM-DD HH:mm')}`,
    '',
    '各 CSV は UTF-8 BOM 付きで、 Excel でそのまま開けます。',
    'コメント行 (# 始まり) は取込時に自動でスキップされます。',
    '',
    '== ファイル一覧 ==',
    ...TEMPLATES.map((t) => `- ${t.filename} [${t.priority}] : ${t.title} — ${t.description}`),
    '',
    '== 推奨導入順序 ==',
    '1. 職種マスタ (job_types)',
    '2. 診療科 (departments)',
    '3. 手術室 (rooms)',
    '4. 休日ポリシー (holiday_policy)',
    '5. 祝日 (holidays)',
    '6. 術式マスタ (procedures)',
    '7. スキルグループ (skill_groups)',
    '8. スタッフ (staff)',
    '9. 勤務パターン (shift_patterns) - スタッフ / 職種登録後',
    '',
  ].join('\n');
  zip.file('README.txt', readme);

  const blob = await zip.generateAsync({ type: 'blob' });
  const zipName = `scrubedge_templates_${dayjs().format('YYYYMMDD_HHmm')}.zip`;
  triggerDownload(blob, zipName);
};

// =============================================================================
// UI
// =============================================================================
export default function OperatorDataFormats() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate(`/${OPERATOR_PATH}`)} style={styles.backBtn}>← ダッシュボード</button>
          <h1 style={styles.title}>📦 新規施設導入 データフォーマット一覧</h1>
        </div>
        <button onClick={downloadAllAsZip} style={styles.allBtn}>📦 ZIP でまとめてダウンロード</button>
      </header>

      <main style={styles.main}>
        <div style={styles.intro}>
          新規施設が ScrubEdge を導入する際に、 事前に準備してもらうデータのテンプレート一覧です。
          <br />
          各テンプレは CSV (UTF-8 BOM 付き) でダウンロードでき、 サンプル行が含まれています。
          <br />
          <strong style={{ color: '#fca5a5' }}>必須</strong> は最低限、
          <strong style={{ color: '#fcd34d' }}>推奨</strong> は初期設定を効率化するためのデータです。
        </div>

        <div style={styles.grid}>
          {TEMPLATES.map((t) => (
            <div key={t.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <span style={{ ...styles.priorityBadge, background: t.priorityColor }}>{t.priority}</span>
                  <h2 style={styles.cardTitle}>{t.title}</h2>
                </div>
                <button onClick={() => downloadCsv(t)} style={styles.dlBtn}>📥 CSV</button>
              </div>
              <p style={styles.cardDesc}>{t.description}</p>
              <table style={styles.colTable}>
                <thead>
                  <tr>
                    <th style={styles.colTh}>列名</th>
                    <th style={styles.colTh}>必須</th>
                    <th style={styles.colTh}>説明</th>
                  </tr>
                </thead>
                <tbody>
                  {t.columns.map((c) => (
                    <tr key={c.name} style={styles.colTr}>
                      <td style={styles.colTd}><code style={styles.code}>{c.name}</code></td>
                      <td style={{ ...styles.colTd, textAlign: 'center' }}>
                        {c.required
                          ? <span style={{ color: '#fca5a5', fontWeight: 700 }}>✓</span>
                          : <span style={{ color: '#64748b' }}>-</span>}
                      </td>
                      <td style={{ ...styles.colTd, color: '#94a3b8', fontSize: 11 }}>{c.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          <h3 style={{ margin: 0, marginBottom: 8, fontSize: 14 }}>📌 導入時の推奨手順</h3>
          <ol style={{ margin: 0, paddingLeft: 20, color: '#94a3b8', fontSize: 13, lineHeight: 1.8 }}>
            <li><strong>「ZIP でまとめてダウンロード」</strong> でテンプレを一括取得 (README.txt 付き)</li>
            <li>各 CSV をお客さん (施設) に送付し、 情報を埋めてもらう</li>
            <li>組織作成後、 各設定画面 (/settings) で対応する CSV をインポート、 または UI で入力</li>
            <li>手術室 / 診療科 / 術式 / 職種 / スタッフ の順で登録するとエラーが少ない</li>
            <li>勤務パターンは診療科・職種登録後に定義</li>
          </ol>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' },
  header: { background: '#1e293b', padding: '16px 24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  backBtn: { padding: '6px 14px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  allBtn: { padding: '8px 16px', background: 'linear-gradient(135deg, #059669, #047857)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  main: { maxWidth: 1200, margin: '0 auto', padding: 24 },
  intro: { background: '#1e293b', padding: 16, borderRadius: 12, border: '1px solid #334155', marginBottom: 20, fontSize: 13, color: '#cbd5e1', lineHeight: 1.7 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 16 },
  card: { background: '#1e293b', borderRadius: 12, padding: 16, border: '1px solid #334155' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 10 },
  cardTitle: { margin: '6px 0 0', fontSize: 15, fontWeight: 700 },
  cardDesc: { fontSize: 11, color: '#94a3b8', margin: '0 0 12px', lineHeight: 1.6 },
  priorityBadge: { padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: 'white' },
  dlBtn: { padding: '6px 12px', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' },
  colTable: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  colTh: { padding: '4px 6px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid #334155' },
  colTr: { borderBottom: '1px solid #263449' },
  colTd: { padding: '4px 6px' },
  code: { background: '#0f172a', padding: '1px 6px', borderRadius: 4, fontSize: 11, color: '#93c5fd', fontFamily: 'Menlo, Consolas, monospace' },
  footer: { marginTop: 24, background: '#1e293b', padding: 16, borderRadius: 12, border: '1px solid #334155' },
};
